import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { getLabById, getLabPollResponses } from "../firebase";
import { formatLabDate, formatLabTimeRange } from "../utils/datetime";
import { normalizeLabPolls } from "../utils/labContent";
import Footer from "../components/Footer";
import SiteHeader from "../components/SiteHeader";

function LabPollResultsPage() {
  const { id, pollId } = useParams();
  const [lab, setLab] = useState(null);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedResponse, setSelectedResponse] = useState(null);

  useEffect(() => {
    let isActive = true;

    const loadData = async () => {
      setIsLoading(true);
      setError("");

      try {
        const nextLab = await getLabById(id);
        if (!nextLab) {
          throw new Error("Lab not found.");
        }

        const nextResponses = await getLabPollResponses({
          labId: id,
          pollId,
          maxResults: 2000,
        });

        if (isActive) {
          setLab(nextLab);
          setResponses(nextResponses);
        }
      } catch (loadError) {
        console.error("Failed to load poll results page:", loadError);
        if (isActive) {
          setError("Could not load poll results right now.");
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isActive = false;
    };
  }, [id, pollId]);

  const poll = useMemo(() => {
    const normalizedPolls = normalizeLabPolls(lab?.polls);
    return normalizedPolls.find((entry) => entry.id === pollId) || null;
  }, [lab, pollId]);

  return (
    <>
      <SiteHeader registerHref={`/lab/${id}`} registerLabel="Back to lab" />

      <main className="lab-page-main poll-results-main">
        <div className="lab-detail-container">
          <Link className="text-link poll-results-back-link" to={`/lab/${id}`}>
            Back to lab
          </Link>

          {isLoading ? <p>Loading poll results...</p> : null}
          {error ? <p className="status-message error">{error}</p> : null}

          {!isLoading && !error && lab ? (
            <article className="card poll-results-card">
              <p className="eyebrow">{formatLabDate(lab.dateTime)}</p>
              <h1 className="poll-results-lab-title">{lab.title}</h1>
              <p className="lab-time poll-results-time">
                {formatLabTimeRange(lab.dateTime, lab.durationMinutes)}
              </p>

              {poll ? (
                <section>
                  <p className="poll-results-line">
                    <span>Poll Results:</span> {poll.question}
                  </p>

                  {responses.length === 0 ? (
                    <p>No responses yet.</p>
                  ) : (
                    <div className="poll-bubble-grid">
                      {responses.map((response, index) => (
                        <button
                          key={response.id}
                          type="button"
                          className="poll-bubble"
                          onClick={() => setSelectedResponse({ ...response, index: index + 1 })}
                        >
                          {response.answer}
                        </button>
                      ))}
                    </div>
                  )}
                </section>
              ) : (
                <p>This poll is not available for this lab.</p>
              )}
            </article>
          ) : null}
        </div>
      </main>

      {selectedResponse ? (
        <div
          className="poll-response-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Poll response detail"
          onClick={() => setSelectedResponse(null)}
        >
          <article className="card poll-response-modal" onClick={(event) => event.stopPropagation()}>
            <button
              type="button"
              className="poll-response-close-btn"
              onClick={() => setSelectedResponse(null)}
              aria-label="Close response detail"
            >
              x
            </button>
            <p className="eyebrow">Response {selectedResponse.index}</p>
            <p className="poll-response-modal-text">{selectedResponse.answer}</p>
          </article>
        </div>
      ) : null}

      <Footer />
    </>
  );
}

export default LabPollResultsPage;
